import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Dot, SettingsAdjust } from '@mint-lib/icons';
import {
  OMNI_MDM_PLATFORMS,
  useFlags,
  useMdmPlatformType,
} from '@mint-lib/routing-context';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TruncateText,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';
import type { MediaPlan } from '@mint-sdk/api-media-plan';
import { getRouteApi, Link } from '@mint/routing';

import { MP_LEVELS } from '../../../../../../consts/index.js';
import { useChosenLevel } from '../../../../../../hooks/useChosenLevel.js';
import { chooseLevel } from '../../../../../../utils/chooseLevel.js';

const routerApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

type MediaRowNameColumnProps = {
  mediaData: MediaPlan.MediaData;
};

export const MediaRowNameColumn = ({ mediaData }: MediaRowNameColumnProps) => {
  const { t } = useTranslation('@myn/planning_v2');

  const ff = useFlags('@mint/arm', ['mdmPlatformsV2']);
  const mdmPlatformsV2Enabled = ff.mdmPlatformsV2.enabled;

  const [isOpen, setIsOpen] = useState(false);
  const { level, id } = useChosenLevel();

  const { campaignId } = routerApi.useParams();
  const { mediaPlan: planMediaDataId } = routerApi.useSearch();

  const { isOmniPlatform, isLoading: isPlatformTypeLoading } =
    useMdmPlatformType({
      enabled: mdmPlatformsV2Enabled,
    });

  const platformName = mediaData.mediaRowProps?.platformName ?? '';
  const legacyOmniPlatformName = getLegacyOmniPlatformName(platformName);

  const isOmni = mdmPlatformsV2Enabled
    ? isOmniPlatform(platformName)
    : Boolean(legacyOmniPlatformName);

  const isCampaignSettingsDisabled =
    !mediaData.mediaRowProps?.isCampaignSettingsAvailable ||
    (mdmPlatformsV2Enabled && isPlatformTypeLoading);

  const campaignSettingsLinkProps = getCampaignSettingsLinkProps(
    campaignId,
    mediaData,
    isOmni,
    platformName,
  );

  return (
    <div className="flex h-7 items-center gap-2">
      {level === MP_LEVELS.FLIGHT ? (
        <Dot
          className={cn(
            'min-w-4',
            mediaData.mediaRowProps?.isConfigured
              ? 'fill-success-main'
              : 'fill-warning-main',
          )}
        />
      ) : (
        <div className="min-w-4" />
      )}
      <TruncateText>
        <p className="m-0 text-sm">{mediaData.label}</p>
      </TruncateText>
      <Tooltip
        open={isOpen && isCampaignSettingsDisabled}
        onOpenChange={setIsOpen}
      >
        <TooltipTrigger asChild>
          <Link
            data-dd-action-name="Planning grid: open campaign settings"
            className="ml-auto flex"
            to={campaignSettingsLinkProps.to}
            params={campaignSettingsLinkProps.params}
            disabled={isCampaignSettingsDisabled}
            preload={campaignSettingsLinkProps.preload}
            search={
              id && planMediaDataId
                ? chooseLevel(
                    { mediaDataId: id, level },
                    { mediaPlan: planMediaDataId },
                  )
                : undefined
            }
          >
            <Button
              variant="text"
              size="small"
              icon
              aria-label="Navigate to campaign settings"
              disabled={isCampaignSettingsDisabled}
            >
              <SettingsAdjust
                className={cn(
                  isCampaignSettingsDisabled && 'fill-text-disabled',
                )}
              />
            </Button>
          </Link>
        </TooltipTrigger>
        <TooltipContent className="z-1 flex-col">
          {t('Add start, end dates and net media budget in a Flight...')}
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

const getCampaignSettingsLinkProps = (
  campaignId: string,
  mediaData: MediaPlan.MediaData,
  isOmni: boolean,
  platformName?: string,
) => {
  if (isOmni) {
    return {
      to: '/campaign/$campaignId/settings/omni/$platform/$tileId/settings/',
      params: {
        campaignId,
        platform: platformName,
        tileId: mediaData.mediaItemId,
      },
      preload: 'intent',
    } as const;
  }

  return {
    to: '/campaign/$campaignId/settings/ghost/$tileId/settings/',
    params: { campaignId, tileId: mediaData.mediaItemId },
  } as const;
};

const getLegacyOmniPlatformName = (platformName: string) =>
  OMNI_MDM_PLATFORMS.find((name) => platformName.includes(name));
